import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Dumbbell,
  BarChart3,
  Users,
  Calendar,
  Zap,
  Shield,
  ChevronRight,
  Flame,
  Trophy,
  ArrowRight,
  Menu,
  X,
  CheckCircle2,
  TrendingUp,
} from 'lucide-react'

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect() } },
      { threshold }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return { ref, visible }
}

function Navbar() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0C0C1D]/90 backdrop-blur-xl shadow-lg shadow-black/30 border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-2.5">
          <div className="h-16 w-16 rounded-2xl overflow-hidden flex-shrink-0">
            <img src="/logo.png" alt="fitbdy" className="w-full h-full object-cover scale-125" />
          </div>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {[
            { label: 'Funcionalidades', href: '#funcionalidades' },
            { label: 'Cómo funciona', href: '#como-funciona' },
            { label: 'Beneficios', href: '#beneficios' },
          ].map(({ label, href }) => (
            <a
              key={label}
              href={href}
              className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
            >
              {label}
            </a>
          ))}
        </div>

        <button className="md:hidden p-2 text-gray-400" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#0C0C1D]/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-4 flex flex-col gap-3">
            {[
              { label: 'Funcionalidades', href: '#funcionalidades' },
              { label: 'Cómo funciona', href: '#como-funciona' },
              { label: 'Beneficios', href: '#beneficios' },
            ].map(({ label, href }) => (
              <a
                key={label}
                href={href}
                className="text-base font-medium text-gray-300 py-2"
                onClick={() => setOpen(false)}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  )
}

function HeroSection() {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-16">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0C0C1D]" />
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-indigo-600/12 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-700/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-indigo-500/6 rounded-full blur-3xl" />

      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: 'radial-gradient(circle, #818CF8 1px, transparent 1px)', backgroundSize: '32px 32px' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm font-medium">
              <Flame className="w-4 h-4 text-orange-400" /> Tu compañero de entrenamiento diario
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.05]">
              Entrená{' '}
              <span className="bg-gradient-to-r from-indigo-400 via-violet-400 to-purple-400 bg-clip-text text-transparent">
                mejor
              </span>
              ,<br />
              cada día
            </h1>

            <p className="text-lg md:text-xl text-gray-400 max-w-xl leading-relaxed">
              Creá tus rutinas, registrá cada serie y peso, seguí tu progreso y conectate con otros.
              Todo lo que necesitás para llevar tu entrenamiento al siguiente nivel.
            </p>

            <div className="flex flex-wrap gap-4">
              {/* Web app temporarily disabled — CTA scrolls to features instead.
                  To revive: change `to="#funcionalidades"` back to `to="/app"`. */}
              <Link
                to="#funcionalidades"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 shadow-xl shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Empezar gratis <ChevronRight className="w-5 h-5" />
              </Link>
              <a
                href="#funcionalidades"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-gray-300 bg-white/5 border border-white/10 hover:border-indigo-500/40 hover:bg-white/10 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Ver funcionalidades
              </a>
            </div>

            <div className="flex items-center gap-6 pt-2">
              {[
                { icon: Dumbbell, label: 'Rutinas personalizadas' },
                { icon: TrendingUp, label: 'Seguimiento de progreso' },
                { icon: Users, label: 'Comunidad fitness' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-gray-500">
                  <Icon className="w-4 h-4 text-indigo-400" />
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual - app mockup */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main card */}
              <div className="bg-[#161630] rounded-2xl shadow-2xl border border-white/5 p-6 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Entrenamiento de hoy</p>
                    <p className="text-xl font-bold text-white mt-0.5">Push A — Pecho & Tríceps</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                    <Dumbbell className="w-6 h-6 text-indigo-400" />
                  </div>
                </div>

                {/* Exercise list */}
                <div className="space-y-2">
                  {[
                    { name: 'Press banca', sets: '4×8', weight: '80 kg', done: true },
                    { name: 'Press inclinado', sets: '3×10', weight: '60 kg', done: true },
                    { name: 'Aperturas', sets: '3×12', weight: '20 kg', done: false },
                    { name: 'Tríceps polea', sets: '4×12', weight: '30 kg', done: false },
                  ].map((ex) => (
                    <div key={ex.name} className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${ex.done ? 'text-emerald-400' : 'text-gray-600'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{ex.name}</p>
                        <p className="text-xs text-gray-500">{ex.sets} · {ex.weight}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Progress bar */}
                <div className="pt-2">
                  <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>Progreso</span>
                    <span>2/4 ejercicios</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full w-1/2 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full" />
                  </div>
                </div>
              </div>

              {/* Floating card - streak */}
              <div className="absolute -left-10 top-1/3 bg-[#161630] rounded-xl shadow-xl border border-white/5 p-4 w-44">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                    <Flame className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">21 días</p>
                    <p className="text-xs text-orange-400">Racha activa 🔥</p>
                  </div>
                </div>
              </div>

              {/* Floating card - PR */}
              <div className="absolute -right-6 -bottom-4 bg-[#161630] rounded-xl shadow-xl border border-white/5 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">¡Nuevo PR!</p>
                    <p className="text-xs text-gray-500">Press banca · 85 kg</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const features = [
  {
    icon: Dumbbell,
    title: 'Rutinas personalizadas',
    description: 'Creá y editá tus rutinas de entrenamiento con días, ejercicios, series y pesos. Totalmente a tu medida.',
    color: 'from-indigo-500 to-violet-600',
    bg: 'bg-indigo-500/10 border-indigo-500/20',
    iconColor: 'text-indigo-400',
  },
  {
    icon: BarChart3,
    title: 'Seguimiento y estadísticas',
    description: 'Visualizá tu progreso con gráficos, records personales (PR) y racha de entrenamientos.',
    color: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-500/10 border-emerald-500/20',
    iconColor: 'text-emerald-400',
  },
  {
    icon: Users,
    title: 'Comunidad social',
    description: 'Seguí a otros usuarios, compartí rutinas y descubrí nuevos entrenamientos de la comunidad.',
    color: 'from-pink-500 to-rose-600',
    bg: 'bg-pink-500/10 border-pink-500/20',
    iconColor: 'text-pink-400',
  },
  {
    icon: Calendar,
    title: 'Calendario de entrenamientos',
    description: 'Planificá y visualizá tus entrenamientos en un calendario mensual con indicadores de completado.',
    color: 'from-cyan-500 to-blue-600',
    bg: 'bg-cyan-500/10 border-cyan-500/20',
    iconColor: 'text-cyan-400',
  },
  {
    icon: Flame,
    title: 'Racha y motivación',
    description: 'Mantené tu racha de días consecutivos y superá tus marcas personales con cada sesión.',
    color: 'from-orange-500 to-amber-600',
    bg: 'bg-orange-500/10 border-orange-500/20',
    iconColor: 'text-orange-400',
  },
  {
    icon: Shield,
    title: 'Seguro y privado',
    description: 'Tus datos de entrenamiento están protegidos con autenticación segura y almacenamiento cifrado.',
    color: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-500/10 border-violet-500/20',
    iconColor: 'text-violet-400',
  },
]

function FeaturesSection() {
  const { ref, visible } = useInView()
  return (
    <section id="funcionalidades" className="relative py-24 md:py-32">
      <div className="absolute inset-0 bg-[#0C0C1D]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-indigo-600/5 rounded-full blur-3xl" />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Funcionalidades</p>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
            Todo para tu entrenamiento
          </h2>
          <p className="text-lg text-gray-400">
            Una app completa para planificar, ejecutar y analizar cada sesión de gym.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`group relative p-6 rounded-2xl border ${f.bg} hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 ${
                visible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className={`w-12 h-12 rounded-xl ${f.bg} border flex items-center justify-center mb-4`}>
                <f.icon className={`w-6 h-6 ${f.iconColor}`} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{f.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

const steps = [
  {
    number: '01',
    title: 'Creá tu cuenta',
    description: 'Registrate gratis en segundos y configurá tu perfil de entrenamiento.',
    icon: Zap,
  },
  {
    number: '02',
    title: 'Armá tus rutinas',
    description: 'Creá rutinas personalizadas con los ejercicios, series y pesos que necesitás.',
    icon: Dumbbell,
  },
  {
    number: '03',
    title: 'Entrenás y registrás',
    description: 'Abrí la app en el gym, completá cada serie y actualizá tu progreso en tiempo real.',
    icon: CheckCircle2,
  },
  {
    number: '04',
    title: 'Analizá tu progreso',
    description: 'Revisá estadísticas, rachas y records personales para mejorar día a día.',
    icon: TrendingUp,
  },
]

function HowItWorksSection() {
  const { ref, visible } = useInView()
  return (
    <section id="como-funciona" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0E0E1E] via-[#161630] to-[#0E0E1E]" />
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: 'radial-gradient(circle, #818CF8 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Cómo funciona</p>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
            Simple, rápido y efectivo
          </h2>
          <p className="text-lg text-gray-400">
            Empezá a entrenar de forma inteligente en cuatro pasos.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`relative text-center ${visible ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: `${i * 120}ms` }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 mb-5">
                <step.icon className="w-7 h-7 text-indigo-400" />
              </div>
              <div className="text-xs font-bold text-indigo-400 tracking-widest mb-2">{step.number}</div>
              <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{step.description}</p>

              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)]">
                  <div className="border-t-2 border-dashed border-indigo-500/15" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

const stats = [
  { value: '100%', label: 'Gratis para usar' },
  { value: '∞', label: 'Rutinas y ejercicios' },
  { value: '6', label: 'Grupos musculares' },
  { value: '24/7', label: 'Disponible siempre' },
]

function BenefitsSection() {
  const { ref, visible } = useInView()
  return (
    <section id="beneficios" className="relative py-24 md:py-32">
      <div className="absolute inset-0 bg-[#0C0C1D]" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-indigo-700/8 rounded-full blur-3xl" />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3">Beneficios</p>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
            Resultados reales
          </h2>
          <p className="text-lg text-gray-400">
            Diseñado para quienes se toman el entrenamiento en serio.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`text-center p-8 rounded-2xl bg-white/3 border border-white/5 hover:border-indigo-500/30 hover:bg-indigo-500/5 transition-all duration-300 ${
                visible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <p className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent mb-2">
                {s.value}
              </p>
              <p className="text-sm text-gray-400 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function CTASection() {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-violet-900/80 to-[#0C0C1D]" />
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />
      <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl" />

      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/20 text-orange-400 text-sm font-medium mb-8">
          <Flame className="w-4 h-4" /> Comenzá tu racha hoy
        </div>
        <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-6">
          Tu mejor versión<br />te está esperando
        </h2>
        <p className="text-lg text-indigo-200 mb-10 max-w-xl mx-auto">
          Unite a quienes ya entrenan con fitbdy. Sin excusas, sin complicaciones.
        </p>
        {/* Web app temporarily disabled — CTA scrolls to features.
            To revive: change `to="#funcionalidades"` back to `to="/app"`. */}
        <Link
          to="#funcionalidades"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-lg font-semibold text-indigo-900 bg-white hover:bg-gray-100 shadow-xl shadow-indigo-900/40 hover:shadow-2xl transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Empezar ahora — es gratis <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="bg-[#08081A] text-gray-500 py-12 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl overflow-hidden opacity-80">
              <img src="/logo.png" alt="fitbdy" className="w-full h-full object-cover scale-125" />
            </div>
          </div>

          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">
              Política de Privacidad
            </Link>
            <a href="mailto:contacto@fitbdy.com" className="hover:text-white transition-colors">
              Contacto
            </a>
            <a
              href="https://www.linkedin.com/in/kevinvinograd"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors inline-flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              LinkedIn
            </a>
          </div>

          <p className="text-sm">&copy; {new Date().getFullYear()} fitbdy. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  )
}

export default function LandingPage() {
  useEffect(() => {
    document.title = 'fitbdy | Tu compañero de entrenamiento diario'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Aplicación de fitness para crear rutinas personalizadas, registrar entrenamientos, seguir tu progreso y conectarte con otros. Gratis para iOS y Android.'
    )
  }, [])

  return (
    <div className="min-h-screen bg-[#0C0C1D] text-white overflow-x-hidden">
      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.55s ease both;
        }
        .animation-delay-100 { animation-delay: 100ms; }
        .animation-delay-200 { animation-delay: 200ms; }
        .animation-delay-300 { animation-delay: 300ms; }
        .animation-delay-400 { animation-delay: 400ms; }
        .animation-delay-500 { animation-delay: 500ms; }
      `}</style>
      <Navbar />
      <main>
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <BenefitsSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  )
}
